import React, { useState, useEffect } from "react";
import { TabHeader, OrnateFrame } from "@/components/royal";
import { EditableText } from "@/components/fields";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Save, Users, Crown, User } from "lucide-react";

const RELATIONSHIPS = [
  "Husband", "Wife", "Brother", "Sister", "Son", "Daughter",
  "Bastard Son", "Bastard Daughter", "Adopted Son", "Other",
];

export default function Nobility({ settings, saveSettings, editable }) {
  const [houses, setHouses] = useState(settings.nobility || []);
  const [dirty, setDirty] = useState(false);
  useEffect(() => { setHouses(settings.nobility || []); setDirty(false); }, [settings.nobility]);

  const mutate = (fn) => { setHouses((prev) => { const n = structuredClone(prev); fn(n); return n; }); setDirty(true); };
  const addHouse = () => mutate((h) => h.push({ id: crypto.randomUUID(), name: "New House", lord: "", members: [] }));
  const rmHouse = (id) => mutate((h) => { const i = h.findIndex((x) => x.id === id); if (i >= 0) h.splice(i, 1); });
  const setField = (id, k, v) => mutate((h) => { const x = h.find((y) => y.id === id); if (x) x[k] = v; });
  const addMember = (id) => mutate((h) => { const x = h.find((y) => y.id === id); if (x) { x.members = x.members || []; x.members.push({ id: crypto.randomUUID(), name: "", relationship: "" }); } });
  const setMember = (id, mid, k, v) => mutate((h) => { const x = h.find((y) => y.id === id); const m = x?.members?.find((z) => z.id === mid); if (m) m[k] = v; });
  const rmMember = (id, mid) => mutate((h) => { const x = h.find((y) => y.id === id); if (x) x.members = x.members.filter((z) => z.id !== mid); });

  const save = async () => { await saveSettings({ nobility: houses }); setDirty(false); };

  return (
    <div className="space-y-6">
      <TabHeader label="The Great Houses" title="Nobility">
        {editable && (
          <>
            <Button variant="outline" size="sm" className="rounded-sm" onClick={addHouse} data-testid="add-house">
              <Plus strokeWidth={1.5} className="w-4 h-4 mr-1" /> House
            </Button>
            <Button size="sm" className="rounded-sm" onClick={save} disabled={!dirty} data-testid="save-nobility">
              <Save strokeWidth={1.5} className="w-4 h-4 mr-1" /> Save for All
            </Button>
          </>
        )}
      </TabHeader>

      {houses.length === 0 ? (
        <div className="ht-panel p-12 text-center text-muted-foreground" data-testid="nobility-empty">No noble houses recorded.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="nobility-grid">
          {houses.map((h, i) => (
            <OrnateFrame key={h.id} hover className="px-6 pt-7 pb-6 space-y-4" testId={`house-card-${i}`}>
              <div className="flex items-center gap-2">
                <Users strokeWidth={1.5} className="w-5 h-5 text-gold" />
                {editable
                  ? <div className="flex-1"><EditableText value={h.name} editable onChange={(v) => setField(h.id, "name", v)} placeholder="House name" testId={`house-name-${i}`} /></div>
                  : <h3 className="ht-title text-2xl flex-1">{h.name}</h3>}
                {editable && (
                  <Button variant="ghost" size="icon" className="rounded-sm h-8 w-8 text-destructive" onClick={() => rmHouse(h.id)} data-testid={`house-remove-${i}`}>
                    <Trash2 strokeWidth={1.5} className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Lord of the House */}
              <div className="ht-panel px-3 py-2.5 flex items-center gap-2">
                <Crown strokeWidth={1.5} className="w-4 h-4 text-gold shrink-0" />
                <div className="ht-uppercase-label shrink-0">Lord of the House</div>
                {editable
                  ? <div className="flex-1"><EditableText value={h.lord} editable onChange={(v) => setField(h.id, "lord", v)} placeholder="Name" testId={`house-lord-${i}`} /></div>
                  : <span className="text-sm flex-1 text-right sm:text-left">{h.lord || "—"}</span>}
              </div>

              {/* Members */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="ht-uppercase-label flex items-center gap-1.5"><User strokeWidth={1.5} className="w-4 h-4" /> Household</div>
                  {editable && (
                    <Button variant="ghost" size="sm" className="rounded-sm h-7" onClick={() => addMember(h.id)} data-testid={`house-add-member-${i}`}>
                      <Plus strokeWidth={1.5} className="w-3.5 h-3.5 mr-1" /> Add Person
                    </Button>
                  )}
                </div>
                {(h.members || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground">—</p>
                ) : (
                  <div className="space-y-2">
                    {(h.members || []).map((m, mi) => (
                      <div key={m.id} className="ht-panel px-3 py-2 flex items-center gap-2 flex-wrap" data-testid={`house-${i}-member-${mi}`}>
                        <User strokeWidth={1.5} className="w-4 h-4 text-gold/60 shrink-0" />
                        {editable ? (
                          <>
                            <div className="flex-1 min-w-[120px]"><EditableText value={m.name} editable onChange={(v) => setMember(h.id, m.id, "name", v)} placeholder="Name" testId={`house-${i}-member-name-${mi}`} /></div>
                            <Select value={m.relationship || undefined} onValueChange={(v) => setMember(h.id, m.id, "relationship", v)}>
                              <SelectTrigger className="rounded-sm bg-background h-8 w-40 text-xs" data-testid={`house-${i}-member-rel-${mi}`}>
                                <SelectValue placeholder="Relationship" />
                              </SelectTrigger>
                              <SelectContent>
                                {RELATIONSHIPS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            {m.relationship === "Other" && (
                              <Input value={m.relationship_custom || ""} onChange={(e) => setMember(h.id, m.id, "relationship_custom", e.target.value)}
                                placeholder="Specify…" className="rounded-sm bg-background h-8 w-40 text-xs" data-testid={`house-${i}-member-other-${mi}`} />
                            )}
                            <Button variant="ghost" size="icon" className="rounded-sm h-7 w-7 text-destructive" onClick={() => rmMember(h.id, m.id)} data-testid={`house-${i}-member-remove-${mi}`}>
                              <Trash2 strokeWidth={1.5} className="w-3.5 h-3.5" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <span className="text-sm flex-1">{m.name || "—"}</span>
                            {(m.relationship === "Other" ? (m.relationship_custom || "Other") : m.relationship) &&
                              <span className="ht-uppercase-label text-gold">{m.relationship === "Other" ? (m.relationship_custom || "Other") : m.relationship}</span>}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </OrnateFrame>
          ))}
        </div>
      )}
    </div>
  );
}
